import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DEFAULT_AXNUI_CONTENT } from "@iddataweb/idw-common-ui/constants";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { resources } from "@constants/hardcoded-strings";
import { MANAGEMENT_CONSOLE_MASTER_DATA_WORKFLOW_TEMPLATES_URL } from "@constants/routes";
import {
    VerificationServiceDetailsReferenceData,
    WORKFLOW_TEMPLATE_DETAILS_REFERENCE_DATA_TYPES
} from "@constants/reference-data";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { ServiceEndpointTypes } from "@enums/service-endpoint-types";
import { formatWorkflowTemplateDetails } from "@formatters/workflow-template-details/workflow-template-details-formatter";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { ApplicationState } from "@idw-types/state/application-state";
import { WorkflowTemplateDetails } from "@idw-types/data/workflow-template-details";
import { setWorkflowTemplateDetails } from "@redux/actions/workflow-template-details-actions";
import {
    getWorkflowTemplateDetailsReduxRequest
} from "@redux-requests/workflow-template-details";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { navigateToUtil } from "@utils/navigation-util";
import { ContentPageHeader } from "../../common/content-page-header/content-page-header";
import { LoadingIndicator } from "../../common/loading-indicator/loading-indicator";
import { CardContainer } from "../../common/card-container/card-container";
import { ContentPageBody } from "../../common/content-page-body/content-page-body";
import { WorkflowTemplateDetailsComponent } from "./workflow-template-details-component/workflow-template-details-component";

export const WorkflowTemplateDetailsContainer = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { workflowTemplateId } = useParams<{ workflowTemplateId: string }>();

    const {
        isLoadingWorkflowTemplateDetails,
        workflowTemplateDetails,
        axnRPServices
    } = useSelector((state: ApplicationState) => ({
        isLoadingWorkflowTemplateDetails: state.workflowTemplateDetails.data.isLoading,
        workflowTemplateDetails: state.workflowTemplateDetails.data.workflowTemplate,
        axnRPServices: state.commonReferenceData.axnRPServices
    }));

    const referenceData = useReferenceDataSelector<VerificationServiceDetailsReferenceData>(WORKFLOW_TEMPLATE_DETAILS_REFERENCE_DATA_TYPES);

    const [shouldLoadDetailsReferenceData, setShouldLoadDetailsReferenceData] = useState(true);
    const [shouldLoadWorkflowTemplateDetails, setShouldLoadWorkflowTemplateDetails] = useState(true);
    const [unformattedWorkflowTemplateDetails, setUnformattedWorkflowTemplateDetails] = useState<WorkflowTemplateDetails>(null);

    const redirectToWorkflowTemplatesPage = () => {
        navigateToUtil(MANAGEMENT_CONSOLE_MASTER_DATA_WORKFLOW_TEMPLATES_URL, navigate, routerLocation.pathname);
    };

    const getUnformattedWorkflowTemplateDetails = async (showError: boolean) => {
        const response = await getWorkflowTemplateDetailsReduxRequest(dispatch)(workflowTemplateId, redirectToWorkflowTemplatesPage, showError);

        if (response) {
            setUnformattedWorkflowTemplateDetails(response);

            if (!referenceData.isLoading.workflowDetails && shouldLoadDetailsReferenceData) {
                setShouldLoadDetailsReferenceData(false);
                getCommonReferenceDataReduxRequest(dispatch)(
                    WORKFLOW_TEMPLATE_DETAILS_REFERENCE_DATA_TYPES,
                    ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                    CommonReferenceDataPageTypes.WORKFLOW_TEMPLATE_DETAILS,
                    true,
                    ServiceEndpointTypes.OIDC,
                    null
                );
            }
        }
    };

    useEffect(() => {
        if (!isLoadingWorkflowTemplateDetails && shouldLoadWorkflowTemplateDetails) {
            setShouldLoadWorkflowTemplateDetails(false);
            getUnformattedWorkflowTemplateDetails(true);
        }
    }, []);

    const isDetailsReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnPropertyTypes &&
            referenceData.axnServiceOfferings &&
            referenceData.rpServiceAttributeProperties &&
            referenceData.rpServiceAttributePropertyMaps &&
            referenceData.serviceOfferingRefreshRates &&
            referenceData.serviceOfferingRefreshRateDefault &&
            referenceData.axnAssertionStatusMaps &&
            referenceData.assertionStatusDefaults &&
            referenceData.assertionScoreAssertionStatusValueMaps &&
            referenceData.trustScoreAssertionStatusValueMaps &&
            referenceData.scoreAttributeMaps &&
            referenceData.assertionScoreServiceOfferings &&
            referenceData.trustScoreServiceOfferings &&
            referenceData.axnAvailableAssertionStatuses &&
            referenceData.axnAssertionAttributes &&
            referenceData.axnAvailablePropertyCategories &&
            referenceData.axnIdentityProviders &&
            referenceData.axnRelyingParties &&
            referenceData.axnAttributeTypes &&
            referenceData.axnAttributeLabels &&
            referenceData.attributeClassifications &&
            referenceData.apAssertions &&
            referenceData.axnAssertions &&
            referenceData.axnServiceOfferingDataSources &&
            referenceData.fallbackServiceMappings &&
            referenceData.deployedPolicyComponents &&
            referenceData.axnAttributeTypeFields &&
            referenceData.simpleDeployedPolicyComponentConditions &&
            referenceData.simpleAXNAdminRPServiceWorkflows &&
            referenceData.axnCompanies &&
            referenceData.simpleAXNWorkflows &&
            referenceData.axnAdminWorkflows &&
            referenceData.documentationLinks &&
            referenceData.axnAcquiredAttributes &&
            referenceData.messagingTemplates &&
            referenceData.availableAssertionScoreValues &&
            referenceData.availableTrustScoreValues &&
            axnRPServices
        );
    }, [
        referenceData.axnPropertyTypes,
        referenceData.axnServiceOfferings,
        referenceData.rpServiceAttributeProperties,
        referenceData.rpServiceAttributePropertyMaps,
        referenceData.serviceOfferingRefreshRates,
        referenceData.serviceOfferingRefreshRateDefault,
        referenceData.axnAssertionStatusMaps,
        referenceData.assertionStatusDefaults,
        referenceData.assertionScoreAssertionStatusValueMaps,
        referenceData.trustScoreAssertionStatusValueMaps,
        referenceData.scoreAttributeMaps,
        referenceData.assertionScoreServiceOfferings,
        referenceData.trustScoreServiceOfferings,
        referenceData.axnAssertionAttributes,
        referenceData.axnAvailablePropertyCategories,
        referenceData.axnIdentityProviders,
        referenceData.axnRelyingParties,
        referenceData.axnAttributeTypes,
        referenceData.axnAttributeLabels,
        referenceData.attributeClassifications,
        referenceData.apAssertions,
        referenceData.axnAssertions,
        referenceData.axnAvailableAssertionStatuses,
        referenceData.axnServiceOfferingDataSources,
        referenceData.fallbackServiceMappings,
        referenceData.deployedPolicyComponents,
        referenceData.axnAttributeTypeFields,
        referenceData.simpleDeployedPolicyComponentConditions,
        referenceData.simpleAXNAdminRPServiceWorkflows,
        referenceData.axnCompanies,
        referenceData.simpleAXNWorkflows,
        referenceData.axnAdminWorkflows,
        referenceData.documentationLinks,
        referenceData.axnAcquiredAttributes,
        referenceData.messagingTemplates,
        referenceData.availableAssertionScoreValues,
        referenceData.availableTrustScoreValues,
        axnRPServices
    ]);

    useEffect(() => {
        if (
            unformattedWorkflowTemplateDetails &&
            isDetailsReferenceDataPresent &&
            !shouldLoadDetailsReferenceData &&
            !referenceData.isLoading.workflowTemplateDetails &&
            !shouldLoadWorkflowTemplateDetails
        ) {
            const formattedWorkflowTemplateDetails = formatWorkflowTemplateDetails(
                unformattedWorkflowTemplateDetails,
                referenceData.axnIdentityProviders,
                referenceData.axnRelyingParties,
                referenceData.axnCompanies,
                referenceData.axnAvailablePropertyCategories,
                referenceData.axnAttributeTypes,
                referenceData.axnAttributeLabels,
                referenceData.attributeClassifications,
                referenceData.rpServiceAttributeProperties,
                referenceData.rpServiceAttributePropertyMaps,
                DEFAULT_AXNUI_CONTENT[LocaleTypes.ENGLISH], //TODO - check this is right
                referenceData.deployedPolicyComponents,
                referenceData.axnAttributeTypeFields,
                referenceData.axnServiceOfferings,
                referenceData.apAssertions,
                referenceData.axnAssertions,
                referenceData.axnServiceOfferingDataSources,
                referenceData.fallbackServiceMappings
            );
            dispatch(setWorkflowTemplateDetails(formattedWorkflowTemplateDetails));
        }
    }, [
        unformattedWorkflowTemplateDetails,
        isDetailsReferenceDataPresent,
        shouldLoadDetailsReferenceData,
        referenceData.isLoading.workflowTemplateDetails,
        shouldLoadWorkflowTemplateDetails
    ]);

    const isLoading = useMemo(() => {
        return (
            isLoadingWorkflowTemplateDetails ||
            referenceData.isLoading.workflowTemplateDetails ||
            shouldLoadWorkflowTemplateDetails ||
            shouldLoadDetailsReferenceData
        );
    }, [
        isLoadingWorkflowTemplateDetails,
        referenceData.isLoading.workflowTemplateDetails,
        shouldLoadWorkflowTemplateDetails,
        shouldLoadDetailsReferenceData
    ]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            workflowTemplateDetails &&
            isDetailsReferenceDataPresent
        );
    }, [
        isLoading,
        workflowTemplateDetails,
        isDetailsReferenceDataPresent
    ]);

    return (
        <CardContainer>
            <ContentPageHeader title={resources.pages.workflowTemplateDetails.title} />
            <ContentPageBody isMaxWidth={true}>
                {isLoaded && (
                    <WorkflowTemplateDetailsComponent
                        workflowTemplateDetails={workflowTemplateDetails}
                        axnIdentityProviders={referenceData.axnIdentityProviders}
                        axnRelyingParties={referenceData.axnRelyingParties}
                        axnCompanies={referenceData.axnCompanies}
                        axnAvailablePropertyCategories={referenceData.axnAvailablePropertyCategories}
                        attributeClassifications={referenceData.attributeClassifications}
                        attributeLabels={referenceData.axnAttributeLabels}
                        attributeTypes={referenceData.axnAttributeTypes}
                        axnuiContent={DEFAULT_AXNUI_CONTENT[LocaleTypes.ENGLISH]} //todo - figure out if this is correct
                        rpServiceAttributeProperties={referenceData.rpServiceAttributeProperties}
                        rpServiceAttributePropertyMaps={referenceData.rpServiceAttributePropertyMaps}
                        axnAttributeTypeFields={referenceData.axnAttributeTypeFields}
                        deployedPolicyComponents={referenceData.deployedPolicyComponents}
                        apAssertions={referenceData.apAssertions}
                        assertions={referenceData.axnAssertions}
                        fallbackServiceMappings={referenceData.fallbackServiceMappings}
                        serviceOfferingDataSources={referenceData.axnServiceOfferingDataSources}
                        serviceOfferings={referenceData.axnServiceOfferings}
                    />
                )}
                {(isLoading) && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};